import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

import { Router } from "@angular/router";
import { Subject } from "rxjs";

import { IPurchase } from "./../../../shared/models/purchase";
import {
  CredentialVerification,
  ICredentialVerification,
} from "./../../../shared/models/products/credential-verification";
import { OtherProduct } from "./../../../shared/models/products/other-product";
import { IProduct } from "./../../../shared/models/products/product";
import { Renewal } from "./../../../shared/models/products/renewal";
import {
  ProductsPayment,
  ProductDetail,
  CredentialVerificationRequest,
} from "./../../../shared/models/payments/products-payment";
import { PurchaseService } from "./../../../shared/services/purchase.service";
import { PurchaseStateService } from "./../../../shared/services/purchase-state.service";
import { CommonApiService } from "./../../../../shared/services/api/common-api.service";

import { Profile } from "./../../../../practitioner/shared/models/profile";
import { RegexPatterns } from "./../../../../shared/regex/regex-patterns";
import { AppSettingsService } from "./../../../../shared/services/app-settings.service";
import { NotificationsService } from "./../../../../shared/services/notifications.service";
import { UserService } from "./../../../../shared/services/user.service";
import { ViewStateService } from "./../../../../shared/services/view-state.service";
import { debounceTime, takeUntil } from "rxjs/operators";
import { environment } from "environments/environment";

enum CardTypes {
  Amex,
  Discover,
  MasterCard,
  Visa,
}

declare let Accept: any;

@Component({
  selector: "app-product-renewal-payment",
  templateUrl: "./product-renewal-payment.component.html",
})
export class ProductRenewalPaymentComponent implements OnInit, OnDestroy {
  purchase: IPurchase;
  checkoutForm: UntypedFormGroup;
  serverMessage: string;

  cardTypes = CardTypes;
  ccType: CardTypes;
  processing = false;
  countries: any;
  currentDate: Date;
  inValidPayment = true;
  showShipping = false;

  paymentType: string;

  profile: any;
  private destroy$ = new Subject<void>();

  constructor(
    private purchaseState: PurchaseStateService,
    private purchaseService: PurchaseService,
    private commonApiService: CommonApiService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private appSettings: AppSettingsService,
    private notificationsService: NotificationsService,
    private userService: UserService,
    private viewState: ViewStateService
  ) {}

  ngOnInit() {
    this.purchase = this.purchaseState.currentState;
    this.viewState.startLoading();
    this.currentDate = new Date();
    this.buildForm();
    this.getCountries();
    this.paymentType = "CC";
    this.purchaseService
      .getProfile(this.userService.CurrentUser.personId)
      .subscribe((res) => {
        this.profile = <Profile>res;
        this.purchaseState.setPractitionerName(
          this.profile.firstName.concat(" ").concat(this.profile.lastName)
        );
        this.formValues.address = this.profile.line1;
        this.formValues.nameOnCard = this.profile.firstName
          .concat(" ")
          .concat(this.profile.lastName);
        this.formValues.city = this.profile.city;
        this.formValues.state = this.profile.stateProvince;
        this.formValues.zip = this.profile.zip;
        this.formValues.countryId = this.profile.countryId.toString();

        this.checkoutForm.patchValue(this.formValues);
        this.checkForShipping(this.purchaseState.currentState.products);

        this.viewState.stopLoading();
      });

      this.purchaseService
        .loadAcceptJs()
        .then(() => {
          console.log("Accept.js is ready");
        })
        .catch((err) => {
          console.error(err);
        });

    this.checkoutForm.controls["cardNumber"].valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((v: string) => {
        if (v.match("^4[0-9]{12}(?:[0-9]{3})?$")) {this.ccType = CardTypes.Visa;}
        else if (
          v.match(
            "^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$"
          )
        )
          {this.ccType = CardTypes.MasterCard;}
        else if (v.match("^3[47][0-9]{13}$")) {this.ccType = CardTypes.Amex;}
        else if (v.match("^6(?:011|5[0-9]{2})[0-9]{12}$"))
          {this.ccType = CardTypes.Discover;}
        else {this.ccType = null;}
      });
  }

  noMoneyDueSubmit() {
    this.processing = true;
    this.viewState.startLoading();

    const products = this.purchaseState.currentState.products;
    const paymentParams = this.mapPurchaseToPayment(products, {dataDescriptor:'', dataValue:''});
    if (this.noMoneyDue()) {
      this.purchaseState.setPaymentMethod("CP");
    } else {
      this.purchaseState.setPaymentMethod(paymentParams.paymentType);
    }
    this.purchaseState.currentState.paymentDate = new Date();
    //this.viewState.stopLoading();
    this.purchaseService
      .sendProductsPayment(paymentParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.viewState.stopLoading();
          this.purchaseState.setCompletedPurchase(
            this.purchaseState.currentState
          );
          this.purchaseState.reset();
          this.router.navigate(["purchase/checkout/confirmation"]);
        },
        (e) => {
          this.handleServerErrorResponse(e);
          this.processing = false;
        }
      );
  }

   private submitPaymentInitial() {
        const authData = {
          clientKey: environment.clientKey,
          apiLoginID: environment.apiLogin,
        };
    
        const cardData = {
          cardNumber: this.formValues.cardNumber,
          month: this.formValues.expirationDate.substring(0, 2),
          year: 20 + this.formValues.expirationDate.substring(2),
          cardCode: this.formValues.ccv,
        };
    
        const secureData = {
          authData,
          cardData,
        };
        //console.log(authData);
        //console.log(cardData);
    
        Accept.dispatchData(secureData, this.responseHandler.bind(this));
      }
    
      responseHandler(response: any) {
        if (response.messages.resultCode === "Error") {
          console.error(
            "Error creating payment nonce/token:",
            response.messages.message
          );
          console.log(response.messages);
          let err = response.messages.message[0].text;
          if (!err || err=="") 
            {err = "Please review credit card information and resubmit";}
          this.notificationsService.error(
            "Payment Failed",
            err
          );        
          this.viewState.stopLoading();
          return;
        }
    
        const opaqueData = response.opaqueData;
        
        this.submitPayment(opaqueData);
      }

  onSubmit(updatedForm) {
    this.processing = true;
    this.formValues = updatedForm;
    const validForm = !this.validateForm();
    if (this.checkoutForm.valid && validForm) {
      this.viewState.startLoading();
      this.submitPaymentInitial();      
    }
  }

  submitPayment(opaqueData) {
      const products = this.purchaseState.currentState.products;
      const paymentParams = this.mapPurchaseToPayment(products, opaqueData);
      if (this.noMoneyDue()) {
        this.purchaseState.setPaymentMethod("CP");
      } else {
        this.purchaseState.setPaymentMethod(paymentParams.paymentType);
      }
      this.purchaseState.currentState.paymentDate = new Date();
      //this.viewState.stopLoading();
      this.purchaseService.sendProductsPayment(paymentParams).subscribe(
        (res) => {
          this.viewState.stopLoading();
          this.purchaseState.setCompletedPurchase(
            this.purchaseState.currentState
          );
          this.purchaseState.reset();
          this.router.navigate(["purchase/checkout/confirmation"]);
        },
        (e) => {
          this.handleServerErrorResponse(e);
          this.processing = false;
        }
      );
  }

  onCancel(event: Event) {
    this.purchaseState.reset();
    this.router.navigate([""]);
  }

  noMoneyDue(): boolean {
    if (
      isNaN(this.purchaseState.currentState.total) ||
      this.purchaseState.currentState.total == 0
    )
      {return true;}
    else {return false;}
  }
  public isDomesticAndCreditCard() {
    let domestic = true;
    if (
      this.formValues.countryId != "1" ||
      this.formValues.paymentType == "ACH"
    )
      {domestic = false;}
    return domestic;
  }

  private getCountries() {
    this.commonApiService.getCountries().subscribe(
      (res) => {
        this.countries = res;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500) {
      this.notificationsService.error(
        "Unexpected Error",
        "An unexpected error occurred."
      );
      if (!this.appSettings.IsProductionMode) {console.log(response);}
    } else {
      this.mapServerErrorsToForm(response.error);
    }

    this.viewState.stopLoading();
  }

  private mapServerErrorsToForm(errors) {
    const unknownErrors = [];
    let firstErrorFieldName;

    for (const error of errors) {
      this.formErrors[error.fieldName] = "";

      const control = this.checkoutForm.get(error.fieldName);
      if (control) {
        if (!firstErrorFieldName) {firstErrorFieldName = error.fieldName;}
        this.formErrors[error.fieldName] += error.message + " ";
      } else {
        unknownErrors.push(error.message);
      }
    }

    if (!errors || errors.message) {
      this.serverMessage = errors ? errors.message : null;
    } else if (unknownErrors.length > 0) {
      this.serverMessage = unknownErrors.join(" ");
    }

    //if (!this.serverMessage)
    this.serverMessage =
      "Your transaction was not processed. Please please check your payment information and try again or contact NBRC.";

    this.viewState.stopLoading();

    this.notificationsService.error(
      "Payment Failed",
      "Your transaction was not processed. Please check your payment information and try again or contact NBRC."
    );

    setTimeout(function () {
      const target = <HTMLElement>(
        (document.getElementById(firstErrorFieldName)
          ? document.getElementById(firstErrorFieldName)
          : document.getElementById("serverMessage"))
      );
      console.log(target);
      target.scrollIntoView();
    }, 750);
  }

  private checkForShipping(products: IProduct[]) {
    const paymentProducts = [];
    const statusCode = "";
    products.forEach((p) => {
      const product = new ProductDetail();
      product.quantity = p.quantity;
      product.serviceCodeID = p.serviceCodeId.toString();

      if (p.constructor === CredentialVerification) {
          //not shippable
      }

      if (p.constructor === OtherProduct) {
        this.showShipping = true;
      }

      if (p.constructor === Renewal) {
        //not shippable
        }      
    });    
  }

  private mapPurchaseToPayment(products: IProduct[], opaqueData) {
    const paymentProducts = [];
    let statusCode = "";
    products.forEach((p) => {
      const product = new ProductDetail();
      product.quantity = p.quantity;
      product.serviceCodeID = p.serviceCodeId.toString();

      if (p.constructor === CredentialVerification) {
        const cv = <ICredentialVerification>p;
        product.cvRequest = new CredentialVerificationRequest();
        product.cvRequest.name = cv.sendTo.name;
        product.cvRequest.addressLine1 = cv.sendTo.line1;
        product.cvRequest.addressLine2 = cv.sendTo.line2;
        product.cvRequest.addressLine3 = cv.sendTo.line3;
        product.cvRequest.agencyID = cv.agencyId;
        product.cvRequest.city = cv.sendTo.city;
        product.cvRequest.stateCode = cv.sendTo.state;
        product.cvRequest.zip = cv.sendTo.postalCode;
        product.cvRequest.cvRequestFor = cv.sendToType;
        product.cvRequest.countryId = cv.sendTo.countryId;
        product.cvRequest.email = cv.sendTo.email;
        product.cost = this.purchaseState.getEffectiveProductPrice(p);
      }

      if (p.constructor === OtherProduct) {
        const op = <OtherProduct>p;
        product.pinInitials = op.initials;
        product.cost = this.purchaseState.getEffectiveProductPrice(p);
        this.showShipping = true;
      }

      if (p.constructor === Renewal) {
        const rp = <Renewal>p;
        products.forEach((p) => {
          product.cost = rp.price;
          product.serviceCodeID = rp.serviceCodeId;
          product.renewalYear = rp.renewalYear;
          product.quantity = 1;
          product.annualFeeCharityId = rp.annualFeeCharityId;
          statusCode = p.status;
        });
      }
      paymentProducts.push(product);
    });

    const payment = new ProductsPayment();
    payment.Details = paymentProducts;
    payment.address = this.formValues.address;
    payment.cardCode = ""; //this.formValues.ccv;
    payment.cardNumber = ""; //this.formValues.cardNumber;
    payment.city = this.formValues.city;
    payment.expirationDate = ""; //this.formValues.expirationDate.replace("/", "");
    payment.payerName = this.formValues.nameOnCard;
    payment.paymentType = this.paymentType;
    payment.personID = this.userService.CurrentUser.personId;
    payment.salesTax = this.purchaseState.currentState.taxAmount;
    payment.state = this.formValues.state;
    payment.totalCost = this.purchaseState.currentState.total;
    payment.zip = this.formValues.zip;
    payment.countryId = this.formValues.countryId;
    payment.createdBy = this.userService.CurrentUser.email;
    payment.statusCode = statusCode;
    payment.accountNumber = this.formValues.accountNumber;
    payment.routingNumber = this.formValues.routingNumber;
    payment.accountType = this.formValues.accountType;
    payment.nameOnAccount = this.formValues.nameOnAccount;
    if (this.paymentType == "CC") {
      payment.dataDescriptor = opaqueData.dataDescriptor;
      payment.dataValue = opaqueData.dataValue;
    }
    if (this.paymentType == "ACH")
      {payment.payerName = this.formValues.nameOnAccount;}

    return payment;
  }

  private buildForm() {
    this.checkoutForm = this.formBuilder.group({
      nameOnCard: [this.formValues.nameOnCard, [Validators.required]],
      cardNumber: [
        this.formValues.cardNumber,
        [
          //Validators.required,
          Validators.maxLength(16),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      expirationDate: [
        this.formValues.expirationDate,
        [
          //Validators.required
        ],
      ],
      ccv: [
        this.formValues.ccv,
        [
          //Validators.required,
          Validators.maxLength(4),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      countryId: [
        this.formValues.countryId,
        // Validators.required
      ],
      address: [
        this.formValues.address,
        [
          //Validators.required
        ],
      ],
      city: [
        this.formValues.city,
        [
          //Validators.required
        ],
      ],
      state: [
        this.formValues.state,
        [
          //Validators.required
        ],
      ],
      zip: [
        this.formValues.zip,
        [
          //Validators.required
        ],
      ],
      paymentType: [this.formValues.paymentType, []],
      accountType: [this.formValues.accountType, []],
      routingNumber: [this.formValues.accountType, []],
      accountNumber: [this.formValues.accountType, []],
      routingNumberConfirm: [this.formValues.accountType, []],
      accountNumberConfirm: [this.formValues.accountType, []],
      nameOnAccount: [this.formValues.accountType, []],
    });
  }

  private validateForm() {
    this.serverMessage = null;

    for (const control in this.checkoutForm.controls) {
      this.checkoutForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    let hasACHErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.checkoutForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (!this.paymentType) {
      this.formErrors["paymentType"] = "Payment method is required.";
      hasErrors = true;
    }
    if (this.paymentType == "ACH") {
      hasACHErrors = this.validateMatches();
    }
    if (hasErrors || hasACHErrors) {
      this.viewState.stopLoading();
      hasErrors = true;
    }
    return hasErrors;
  }

  public isInvalidPayment() {
    if (this.paymentType == "ACH") {
      if (
        this.checkoutForm.value.accountNumber == "" ||
        this.checkoutForm.value.accountNumberConfirm == "" ||
        this.checkoutForm.value.routingNumber == "" ||
        this.checkoutForm.value.routingNumberConfirm == "" ||
        this.checkoutForm.value.nameOnAccount == "" ||
        this.checkoutForm.value.accountType == ""
      )
        {this.inValidPayment = true;}
      else {
        this.inValidPayment = false;
      }
    } else if (this.paymentType == "CC") {
      if (
        this.checkoutForm.value.nameOnCard == "" ||
        this.checkoutForm.value.cardNumber == ""
      ) {
        this.inValidPayment = true;
      } else {
        this.inValidPayment = false;
      }
    } else {
      this.inValidPayment = true;
    }
    return this.inValidPayment;
  }

  private validateMatches() {
    let hasErrors = false;
    if (this.formValues.accountNumber != this.formValues.accountNumberConfirm) {
      this.formErrors["accountNumberConfirm"] = "account number must match";
      hasErrors = true;
    }
    if (this.formValues.routingNumber != this.formValues.routingNumberConfirm) {
      this.formErrors["routingNumberConfirm"] = "routing number must match";
      hasErrors = true;
    }
    return hasErrors;
  }

  formErrors = {
    nameOnCard: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
    accountType: "",
    routingNumber: "",
    accountNumber: "",
    routingNumberConfirm: "",
    accountNumberConfirm: "",
    nameOnAccount: "",
    paymentType: "",
  };

  private validationMessages = {
    nameOnCard: {
      requiredIf: "Name on Card is required.",
      requiredIfNot: "Name on Card is required.",
    },
    cardNumber: {
      requiredIf: "Card Number is required.",
      requiredIfNot: "Card Number is required.",
      pattern: "Card Number is invalid.",
    },
    expirationDate: {
      requiredIf: "Expiration is required.",
      requiredIfNot: "Expiration is required.",
    },
    ccv: {
      requiredIf: "CCV is required.",
      requiredIfNot: "CCV is required.",
      pattern: "CCV is invalid.",
    },
    address: {
      requiredIf: "Address is required.",
    },
    city: {
      requiredIf: "City is required.",
    },
    state: {
      requiredIf: "State is required.",
    },
    zip: {
      requiredIf: "Postal Code is required.",
    },
    countryId: {
      requiredIf: "Country is required.",
    },
    routingNumber: {
      requiredIf: "Routing Number is required.",
      minlength: "Routing Number is invalid.",
    },
    routingNumberConfirm: {
      requiredIf: "Re-enter routing number.",
      minlength: "Routing Number is invalid.",
    },
    accountNumber: {
      requiredIf: "Account Number is required.",
      minlength: "Account Number is invalid.",
    },
    accountNumberConfirm: {
      requiredIf: "Re-enter account number.",
      minlength: "Account Number is invalid.",
    },
    accountType: {
      requiredIf: "Account type is required.",
      minlength: "Account type is invalid.",
    },
    nameOnAccount: {
      requiredIf: "Name on account is required.",
      minlength: "Name on account is invalid.",
    },
    paymentType: {
      required: "Payment method required.",
    },
  };

  private formValues = {
    nameOnCard: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
    paymentType: "",
    routingNumber: "",
    routingNumberConfirm: "",
    accountType: "",
    accountNumber: "",
    accountNumberConfirm: "",
    nameOnAccount: "",
  };

  changePaymentType(paymentType) {
    if (this.paymentType != paymentType) {
      //reset
      if (paymentType == "CH") {
        this.formValues.nameOnCard = null;
        this.formValues.cardNumber = null;
        this.formValues.expirationDate = null;
        this.formValues.ccv = null;
        this.formValues.city = null;
        this.formValues.state = null;
        this.formValues.zip = null;
      }
      this.paymentType = paymentType;
      this.checkoutForm.patchValue(this.formValues);
    }
    this.paymentType = paymentType;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
